import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import { FlightCard, JsonsData } from '@/src/types/flight'
import { getFormattedDate } from '@/src/utils/date'
import { getRouteSummaries } from '@/src/utils/srp/routeUtils'
import { Circle } from '@mui/icons-material'
import { alpha, Avatar, Box, Button, Stack, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import SelectedOnwardFlightInfo from './selectedOnwardFlightInfo'
import SelectedOnwardPriceInfo from './selectedOnwardPriceInfo'

interface StepTabsProps {
    onwardFlightData: FlightCard | null
    onChangeOnwardSelection: () => void
    jsons?: JsonsData
    selectedJourneyLeg: number
}





const StepTabs: React.FC<StepTabsProps> = ({
    onwardFlightData,
    onChangeOnwardSelection,
    jsons,
    selectedJourneyLeg
}) => {
    const locale = useLocale()
    const isArabic = locale === 'ar'
    const tCommon = useTranslations('common')
    const stepLabel = useMemo(() => {
        return {
            [0]: tCommon('chooseDepartureFlight')?.toLocaleUpperCase(),
            [1]: tCommon('chooseReturnFlight')?.toLocaleUpperCase()
        }
    }, [selectedJourneyLeg])
    const { formatDate } = useFormatDate()
    const { urlQueryObj } = useGetSearchParamsObj()
    const depart_date = urlQueryObj?.depart_date
    const return_date = urlQueryObj?.return_date
    const routes = useMemo(() => {
        const sectionArr = Object.values(jsons?.secs || {})
        const route = getRouteSummaries(sectionArr, jsons?.ap || {}, isArabic)
        return route
    }, [jsons?.secs, jsons?.ap, isArabic])

    const dates = useMemo(() => {
        const departureDate = formatDate(getFormattedDate(depart_date, 'MM/DD/YYYY'), 'ddd, D MMM')
        const returnDate = formatDate(getFormattedDate(return_date, 'MM/DD/YYYY'), 'ddd, D MMM')
        return [departureDate, returnDate]
    }, [depart_date, return_date])

    return (
        <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'stretch'}
            border={'1px solid #E4E7EB'}
            mb={2}
            height={"65px"}
            position={"relative"}
            borderRadius={1}
        // padding={2}
        >
            {routes.map((route, index) => {
                const isActive = index === selectedJourneyLeg
                const isOnward = index === 0
                return (
                    <Box
                        key={index}
                        bgcolor={isActive ? theme => alpha(theme.palette.primary.main, 0.1) : 'transparent'}
                        flexBasis={"50%"}
                        display={'flex'}
                        gap={1}
                        sx={{
                            borderRadius: index === 0
                                ? '8px 0 0 8px' // Left tab active: right border radius 0
                                : '0 8px 8px 0' // Right tab active: left border radius 0,


                        }}
                        borderBottom={isActive ? "2px solid #1d4ed8b2" : "none"}
                        position={"relative"}

                    >
                        <Box display={'flex'} gap={1} alignItems={'center'} ml={isOnward ? 2 : 6} width={"100%"}>
                            <Box>
                                <Avatar
                                    sx={{
                                        width: '24px',
                                        height: '24px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: theme => isActive ? theme.palette.primary.main : "#000",
                                        borderColor: theme => isActive ? theme.palette.primary.main : "#000",
                                        borderWidth: "1px",
                                        borderStyle: "solid",
                                        bgcolor: "transparent"
                                    }}>{index + 1}</Avatar>
                            </Box>
                            <Stack spacing={0.2}>
                                {isOnward && onwardFlightData ?
                                    <>
                                        <Typography fontWeight={400} fontSize={12} color='#1F1F1F'>
                                            {route} <Circle sx={{ fontSize: 3, color: '#1F1F1F', fontWeight: 400 }} /> {dates[index]}
                                        </Typography>

                                        {/* will create */}
                                        <Box display={"flex"} gap={0.5} alignItems={'center'}  >
                                            <SelectedOnwardFlightInfo flightData={onwardFlightData} />

                                            <SelectedOnwardPriceInfo flightData={onwardFlightData} />

                                            <Button
                                                size="small"
                                                variant="text"
                                                // sx={{ fontWeight: 600 }}
                                                onClick={onChangeOnwardSelection}
                                                sx={{
                                                    p: 0,
                                                    fontWeight: 700,
                                                    // ml: 2,
                                                    position: "absolute",
                                                    right: 5,
                                                    bottom: 10,
                                                    zIndex: 1,
                                                    textTransform: "capitalize"
                                                }}
                                            >
                                                {tCommon('change')?.toLocaleLowerCase()}
                                            </Button>
                                        </Box>
                                    </>

                                    :
                                    <>
                                        <Typography fontWeight={400} fontSize={12} color='#1F1F1F'>
                                            {stepLabel[index as keyof typeof stepLabel]}
                                        </Typography>


                                        <Typography
                                            fontWeight={600}
                                            fontSize={14}
                                            color='#1F1F1F'>
                                            {route}
                                            <Circle sx={{ fontSize: 4, color: '#1F1F1F', mx: 0.5 }} />
                                            {dates[index]}
                                        </Typography>

                                    </>
                                }

                            </Stack>
                        </Box>
                    </Box>
                )
            })}



            {/*we are rotating a square to 45 deg , and to make the corners exactly touch the top and bottom, the diagonal needs to equal the parent height (65px), so the side length should be 65px / √2 ≈ 45.96px. and we are using 62px + border width 2px = 64px */}
            <Box position={"absolute"}
                height={"calc(62px / 1.4142)"}
                width={"calc(62px / 1.4142)"}
                borderRight={((selectedJourneyLeg === 0 && !isArabic) || (selectedJourneyLeg === 1 && isArabic)) ? "2px solid #1d4ed8b2" : "none"}
                borderTop={isArabic ? "none" : selectedJourneyLeg === 1 ? "2px solid #1d4ed8b2" : "none"}

                borderBottom={!isArabic ? "none" : selectedJourneyLeg === 0 ? "2px solid #1d4ed8b2" : "none"}
                top={"50%"}
                left={"50%"}
                bgcolor={selectedJourneyLeg === 0 ? "#E5E9F1" : '#FAFAFA'}
                sx={{
                    transform: "translate(-50%, -50%) rotate(45deg)",
                }}
            >

            </Box>
        </Box>
    )
}

export default StepTabs
